import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { tipoBadgeClass } from './OtTarjeta';
import { historialAprobaciones, folioTexto } from '../../utils/ordenesTrabajo';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

const field =
  'rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm outline-none bg-white ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

const fechaHora = (ms) => (ms ? new Date(Number(ms)).toLocaleString('es-MX', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

/**
 * Every approval and rejection in the org, newest first, with who and why.
 * Fran (2026-10-19): "necesitamos ver todas las aprobaciones que se van
 * haciendo". Read from `aprobaciones[]` on each OT, open or closed.
 *
 * @param {Object} props
 * @param {Array<Object>} props.rows  - every decorated OT
 * @param {Array<Object>} props.miembros
 * @param {Function} props.onOpen  - (ot)
 */
export function OtAprobaciones({ rows, miembros, onOpen }) {
  const [resultado, setResultado] = useState('');
  const [quien, setQuien] = useState('');
  const todas = useMemo(() => historialAprobaciones(rows || []), [rows]);
  const aprobadores = useMemo(() => {
    const ids = [...new Set(todas.map((a) => a.por).filter(Boolean))];
    return ids.map((id) => ({ id, nombre: miembros.find((m) => m.id === id)?.nombre || todas.find((a) => a.por === id)?.nombre || id }));
  }, [todas, miembros]);
  const filas = useMemo(() => todas.filter((a) => (!resultado || a.resultado === resultado) && (!quien || a.por === quien)), [todas, resultado, quien]);
  const conteo = useMemo(() => ({
    aprobadas: todas.filter((a) => a.resultado === 'aprobada').length,
    rechazadas: todas.filter((a) => a.resultado === 'rechazada').length,
  }), [todas]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={resultado} onChange={(e) => setResultado(e.target.value)} className={field}>
          <option value="">Aprobadas y rechazadas</option>
          <option value="aprobada">Solo aprobadas</option>
          <option value="rechazada">Solo rechazadas</option>
        </select>
        <select value={quien} onChange={(e) => setQuien(e.target.value)} className={field}>
          <option value="">Cualquier aprobador</option>
          {aprobadores.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <span className="text-xs text-zinc-500 tabular-nums ml-1">
          {conteo.aprobadas} aprobadas · {conteo.rechazadas} rechazadas
        </span>
      </div>

      {filas.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-zinc-400 bg-white rounded-lg border border-zinc-200">
          <ShieldCheckIcon className="w-10 h-10" />
          <span className="text-sm text-zinc-500">Sin aprobaciones todavía.</span>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="px-3 py-2 whitespace-nowrap">Cuándo</th>
                <th className="px-3 py-2 w-full max-w-0">Orden</th>
                <th className="px-3 py-2 whitespace-nowrap">Fase</th>
                <th className="px-3 py-2 whitespace-nowrap">Resultado</th>
                <th className="px-3 py-2 whitespace-nowrap">Quién</th>
                <th className="px-3 py-2">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filas.map((a, i) => (
                <tr key={`${a.ot.id}-${a.at}-${i}`} className="hover:bg-zinc-50 cursor-pointer" onClick={() => onOpen?.(a.ot)}>
                  <td className="px-3 py-2.5 text-zinc-600 tabular-nums whitespace-nowrap">{fechaHora(a.at)}</td>
                  <td className="px-3 py-2.5 max-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold tabular-nums text-azul-surelia whitespace-nowrap">{folioTexto(a.ot.folio)}</span>
                      <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap', tipoBadgeClass(a.ot.tipo))}>{a.ot.tipoNombre || a.ot.tipo}</span>
                      <span className="text-zinc-800 truncate min-w-0" title={a.ot.titulo}>{a.ot.titulo}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-600 whitespace-nowrap">{a.faseNombre || a.faseId || '—'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', a.resultado === 'aprobada' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                      {a.resultado === 'aprobada' ? 'Aprobada' : 'Rechazada'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-700 whitespace-nowrap">{miembros.find((m) => m.id === a.por)?.nombre || a.nombre || '—'}</td>
                  <td className="px-3 py-2.5 text-zinc-600">{a.motivo || <span className="text-zinc-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OtAprobaciones;
